import type { ICallToAction, ITypography } from '@jazel/jazel-vuetify'
import type {
  ICardSettings,
  IGlobalSettings,
  IHeaderSettings,
  IFeaturesSectionSettings,
  IFeaturesSectionHeaderSettings,
  IFeaturesSectionGeneralSettings,
} from '@/types'

// Return default global settings
export const getDefaultGlobalSettings = (accountSettings: any): IGlobalSettings => {
  return {
    header: getDefaultHeaderSettings(),
    primaryCTA: getDefaultCTASettings('View Inventory', accountSettings.websiteUrl),
    secondaryCTA: getDefaultCTASettings('Order Now', accountSettings.websiteUrl),
    trimCard: getDefaultTrimCardSettings(),
  }
}

export const getDefaultHeaderSettings = (): any => {
  return {
    modelName: {
      mobile: {
        fontSize: '32',
        fontSizeUnit: 'px',
      },
      desktop: {
        fontSize: '32',
        fontSizeUnit: 'px',
      },
    },
    price: {
      mobile: {
        fontSize: '32',
        fontSizeUnit: 'px',
      },
      desktop: {
        fontSize: '32',
        fontSizeUnit: 'px',
      },
    },
    trimStyle: {
      mobile: {
        fontSize: '18',
        fontSizeUnit: 'px',
      },
      desktop: {
        fontSize: '18',
        fontSizeUnit: 'px',
      },
    },
  }
}

export const getDefaultCTASettings = (label: string = '', link: string = ''): any => {
  return {
    label: label,
    link: link,
    colors: {
      color: '#FFFFFF',
      background: '#0000FF',
      colorHover: '#FFFFFF',
      backgroundHover: '#0000FF',
    },
    icon: {
      colors: {
        color: '#000000',
        colorHover: '#000000',
        background: 'transparent',
        backgroundHover: 'transparent',
      },
      sizes: {
        container: '30',
        containerUnit: 'px',
        icon: '30',
        iconUnit: 'px',
      },
    },
    border: {
      width: '1',
      widthUnit: 'px',
      style: 'solid',
      color: '#000000',
      hoverColor: '#000000',
      radius: '0',
      radiusUnit: 'px',
    },
    typography: {
      fontFamily: 'Arial, sans-serif',
      fontSize: '14',
      fontSizeUnit: 'px',
      fontStyle: 'normal',
      fontWeight: 'normal',
      letterSpacing: '0',
      letterSpacingUnit: 'px',
      lineHeight: '1.5',
      lineHeightUnit: 'em',
      textDecoration: 'none',
      textTransform: 'none',
      textAlignment: 'left',
    },
  }
}

export const getDefaultTrimCardSettings = (): any => {
  return {
    width: '340',
    widthUnit: 'px',
    height: '380',
    heightUnit: 'px',
    imageHeight: '200',
    imageHeightUnit: 'px',
    margin: '4',
    marginUnit: 'px',
    borderRadius: '0',
    borderRadiusUnit: 'px',
  }
}

// Ensure header settings
export const ensureHeaderSettings = (headerSettings: any): IHeaderSettings => {
  const defaultHeaderSettings = getDefaultHeaderSettings()
  if (headerSettings === null || typeof headerSettings !== 'object') {
    return defaultHeaderSettings
  }

  const newHeaderSettings = { ...headerSettings }

  Object.keys(headerSettings).forEach((key: string) => {
    if (
      (headerSettings[key] === null || typeof headerSettings[key] !== 'object') &&
      key in defaultHeaderSettings
    ) {
      newHeaderSettings[key] = defaultHeaderSettings[key]
    }

    Object.keys(headerSettings[key]).forEach((subKey: string) => {
      if (
        (headerSettings[key][subKey] === null || typeof headerSettings[key][subKey] !== 'object') &&
        subKey in defaultHeaderSettings[key]
      ) {
        newHeaderSettings[key][subKey] = defaultHeaderSettings[key][subKey]
      }

      newHeaderSettings[key][subKey] = {
        ...defaultHeaderSettings[key][subKey],
        ...headerSettings[key][subKey],
      }
    })
  })

  return newHeaderSettings
}

// Ensure primary CTA settings
export const ensurePrimayCTASettings = (primaryCTA: any, accountSettings: any): ICallToAction => {
  const defaultPrimaryCTA = getDefaultCTASettings('View Inventory', accountSettings.websiteUrl)

  if (primaryCTA === null || typeof primaryCTA !== 'object') {
    return defaultPrimaryCTA
  }

  const newPrimaryCTA = { ...primaryCTA }

  // Check label
  if (primaryCTA.label === null || primaryCTA.label === '') {
    newPrimaryCTA.label = defaultPrimaryCTA.label
  }

  // Check link
  if (primaryCTA.link === null || primaryCTA.link === '') {
    newPrimaryCTA.link = defaultPrimaryCTA.link
  }

  // Check colors
  if (primaryCTA.colors === null || typeof primaryCTA.colors !== 'object') {
    newPrimaryCTA.colors = defaultPrimaryCTA.colors
  } else {
    Object.keys(primaryCTA.colors).forEach((key: string) => {
      if (primaryCTA.colors[key] === null || primaryCTA.colors[key] === '') {
        newPrimaryCTA.colors[key] = defaultPrimaryCTA.colors[key]
      }
    })
  }

  // Check icon
  if (primaryCTA.icon === null || typeof primaryCTA.icon !== 'object') {
    newPrimaryCTA.icon = defaultPrimaryCTA.icon
  } else {
    // Check icon colors
    if (primaryCTA.icon.colors === null || typeof primaryCTA.icon.colors !== 'object') {
      newPrimaryCTA.icon.colors = defaultPrimaryCTA.icon.colors
    } else {
      Object.keys(primaryCTA.icon.colors).forEach((key: string) => {
        if (primaryCTA.icon.colors[key] === null || primaryCTA.icon.colors[key] === '') {
          newPrimaryCTA.icon.colors[key] = defaultPrimaryCTA.icon.colors[key]
        }
      })
    }

    // Check icon sizes
    if (primaryCTA.icon.sizes === null || typeof primaryCTA.icon.sizes !== 'object') {
      newPrimaryCTA.icon.sizes = defaultPrimaryCTA.icon.sizes
    } else {
      Object.keys(primaryCTA.icon.sizes).forEach((key: string) => {
        if (primaryCTA.icon.sizes[key] === null || primaryCTA.icon.sizes[key] === '') {
          newPrimaryCTA.icon.sizes[key] = defaultPrimaryCTA.icon.sizes[key]
        }
      })
    }
  }

  // Check border
  if (primaryCTA.border === null || typeof primaryCTA.border !== 'object') {
    newPrimaryCTA.border = defaultPrimaryCTA.border
  } else {
    Object.keys(primaryCTA.border).forEach((key: string) => {
      if (primaryCTA.border[key] === null || primaryCTA.border[key] === '') {
        newPrimaryCTA.border[key] = defaultPrimaryCTA.border[key]
      }
    })
  }

  // Check typography
  if (primaryCTA.typography === null || typeof primaryCTA.typography !== 'object') {
    newPrimaryCTA.typography = defaultPrimaryCTA.typography
  } else {
    Object.keys(primaryCTA.typography).forEach((key: string) => {
      if (primaryCTA.typography[key] === null || primaryCTA.typography[key] === '') {
        newPrimaryCTA.typography[key] = defaultPrimaryCTA.typography[key]
      }
    })
  }

  return newPrimaryCTA
}

// Ensure secondary CTA settings
export const ensureSecondaryCTASettings = (
  secondaryCTA: any,
  accountSettings: any,
): ICallToAction => {
  const defaultSecondaryCTA = getDefaultCTASettings('Order Now', accountSettings.websiteUrl)

  if (secondaryCTA === null || typeof secondaryCTA !== 'object') {
    return defaultSecondaryCTA
  }

  const newSecondaryCTA = { ...secondaryCTA }

  // Check label
  if (secondaryCTA.label === null || secondaryCTA.label === '') {
    newSecondaryCTA.label = defaultSecondaryCTA.label
  }

  // Check link
  if (secondaryCTA.link === null || secondaryCTA.link === '') {
    newSecondaryCTA.link = defaultSecondaryCTA.link
  }

  // Check colors
  if (secondaryCTA.colors === null || typeof secondaryCTA.colors !== 'object') {
    newSecondaryCTA.colors = defaultSecondaryCTA.colors
  } else {
    Object.keys(secondaryCTA.colors).forEach((key: string) => {
      if (secondaryCTA.colors[key] === null || secondaryCTA.colors[key] === '') {
        newSecondaryCTA.colors[key] = defaultSecondaryCTA.colors[key]
      }
    })
  }

  // Check icon
  if (secondaryCTA.icon === null || typeof secondaryCTA.icon !== 'object') {
    newSecondaryCTA.icon = defaultSecondaryCTA.icon
  } else {
    // Check icon colors
    if (secondaryCTA.icon.colors === null || typeof secondaryCTA.icon.colors !== 'object') {
      newSecondaryCTA.icon.colors = defaultSecondaryCTA.icon.colors
    } else {
      Object.keys(secondaryCTA.icon.colors).forEach((key: string) => {
        if (secondaryCTA.icon.colors[key] === null || secondaryCTA.icon.colors[key] === '') {
          newSecondaryCTA.icon.colors[key] = defaultSecondaryCTA.icon.colors[key]
        }
      })
    }

    // Check icon sizes
    if (secondaryCTA.icon.sizes === null || typeof secondaryCTA.icon.sizes !== 'object') {
      newSecondaryCTA.icon.sizes = defaultSecondaryCTA.icon.sizes
    } else {
      Object.keys(secondaryCTA.icon.sizes).forEach((key: string) => {
        if (secondaryCTA.icon.sizes[key] === null || secondaryCTA.icon.sizes[key] === '') {
          newSecondaryCTA.icon.sizes[key] = defaultSecondaryCTA.icon.sizes[key]
        }
      })
    }
  }

  // Check border
  if (secondaryCTA.border === null || typeof secondaryCTA.border !== 'object') {
    newSecondaryCTA.border = defaultSecondaryCTA.border
  } else {
    Object.keys(secondaryCTA.border).forEach((key: string) => {
      if (secondaryCTA.border[key] === null || secondaryCTA.border[key] === '') {
        newSecondaryCTA.border[key] = defaultSecondaryCTA.border[key]
      }
    })
  }

  // Check typography
  if (secondaryCTA.typography === null || typeof secondaryCTA.typography !== 'object') {
    newSecondaryCTA.typography = defaultSecondaryCTA.typography
  } else {
    Object.keys(secondaryCTA.typography).forEach((key: string) => {
      if (secondaryCTA.typography[key] === null || secondaryCTA.typography[key] === '') {
        newSecondaryCTA.typography[key] = defaultSecondaryCTA.typography[key]
      }
    })
  }

  return newSecondaryCTA
}

// Ensure trim card settings
export const ensureTrimCardSettings = (trimCard: any): ICardSettings => {
  const defaultTrimCardSettings = getDefaultTrimCardSettings()

  if (trimCard === null || typeof trimCard !== 'object') {
    return defaultTrimCardSettings
  }

  const newTrimCard = { ...trimCard }

  Object.keys(trimCard).forEach((key: string) => {
    if ((trimCard[key] === null || trimCard[key] === '') && key in defaultTrimCardSettings) {
      newTrimCard[key] = defaultTrimCardSettings[key]
    }
  })

  return newTrimCard
}

// Return default features section settings
export const getDefaultFeaturesSectionSettings = (): IFeaturesSectionSettings => {
  return {
    header: getDefaultFeaturesSectionHeaderSettings(),
    features: [],
    generalSettings: getDefaultFeaturesSectionGeneralSettings(),
  }
}

export const getDefaultFeaturesSectionHeaderSettings = (): IFeaturesSectionHeaderSettings => {
  return {
    text: 'New Section',
    colors: {
      color: '#ffffff',
      colorHover: '#ffffff',
      background: '#000000',
      backgroundHover: '#000000',
    },
    typography: {
      fontFamily: 'Arial, sans-serif',
      fontSize: '32',
      fontSizeUnit: 'px',
      fontStyle: 'normal',
      fontWeight: 'normal',
      letterSpacing: '0',
      letterSpacingUnit: 'px',
      lineHeight: '1.5',
      lineHeightUnit: 'em',
      textDecoration: 'none',
      textTransform: 'none',
      textAlignment: 'center',
    },
  }
}

export const getDefaultFeaturesSectionGeneralSettings = (): IFeaturesSectionGeneralSettings => {
  return {
    backgroundColors: {
      color: '#000000',
      colorHover: '#000000',
      background: '#9E9E9E',
      backgroundHover: '#9E9E9E',
    },
  }
}

// Ensure features section settings
export const ensureHighlightedFeaturesSectionSettings = (settings: any) => {
  // If null/undefined, return empty array
  if (!settings) {
    return []
  }

  // If it's already an array, ensure each item has the correct structure
  if (Array.isArray(settings)) {
    return settings.map((section) => ensureFeaturesSectionSettings(section))
  }

  // If it's an object (single section), wrap it in an array
  if (typeof settings === 'object') {
    return [ensureFeaturesSectionSettings(settings)]
  }

  // Fall back to empty array if none of the above
  return []
}

export const ensureFeaturesSectionSettings = (section: any): IFeaturesSectionSettings => {
  // Default section if null/undefined
  if (!section || typeof section !== 'object') {
    return getDefaultFeaturesSectionSettings()
  }

  const newSection = { ...section }

  // Ensure each property exists
  newSection.header = ensureFeaturesSectionHeaderSettings(section.header)
  newSection.generalSettings = ensureFeaturesSectionGeneralSettings(section.generalSettings)

  // IMPORTANT: Ensure features is always an array
  if (!Array.isArray(newSection.features)) {
    newSection.features = []
  }

  return newSection
}

export const ensureFeaturesSectionHeaderSettings = (
  headerSettings: any,
): IFeaturesSectionHeaderSettings => {
  const defaultHeaderSettings = getDefaultFeaturesSectionHeaderSettings()

  if (headerSettings == null || typeof headerSettings !== 'object') {
    return defaultHeaderSettings
  }

  const newHeaderSettings = { ...headerSettings }

  // Check text
  if (headerSettings.text == null || headerSettings.text === '') {
    newHeaderSettings.text = defaultHeaderSettings.text
  }

  // Check colors
  if (headerSettings.colors === null || typeof headerSettings.colors !== 'object') {
    newHeaderSettings.colors = defaultHeaderSettings.colors
  } else {
    Object.keys(headerSettings.colors).forEach((key: string) => {
      if (
        (headerSettings.colors[key] === null || headerSettings.colors[key] === '') &&
        key in defaultHeaderSettings.colors
      ) {
        newHeaderSettings.colors[key] = defaultHeaderSettings.colors[key]
      }
    })
  }

  // Check typography
  if (headerSettings.typography === null || typeof headerSettings.typography !== 'object') {
    newHeaderSettings.typography = defaultHeaderSettings.typography
  } else {
    Object.keys(headerSettings.typography).forEach((key: string) => {
      const typedKey = key as keyof ITypography
      if (
        (headerSettings.typography[typedKey] === null ||
          headerSettings.typography[typedKey] === '') &&
        typedKey in defaultHeaderSettings.typography
      ) {
        newHeaderSettings.typography[typedKey] = defaultHeaderSettings.typography[typedKey]
      }
    })
  }

  return newHeaderSettings
}

export const ensureFeaturesSectionGeneralSettings = (
  generalSettings: any,
): IFeaturesSectionGeneralSettings => {
  const defaultGeneralSettings = getDefaultFeaturesSectionGeneralSettings()

  if (generalSettings === null || typeof generalSettings !== 'object') {
    return defaultGeneralSettings
  }

  const newGeneralSettings = { ...generalSettings }

  // Check backgroundColors
  if (
    generalSettings.backgroundColors === null ||
    typeof generalSettings.backgroundColors !== 'object'
  ) {
    newGeneralSettings.backgroundColors = defaultGeneralSettings.backgroundColors
  } else {
    Object.keys(generalSettings.backgroundColors).forEach((key: string) => {
      if (
        (generalSettings.backgroundColors[key] === null ||
          generalSettings.backgroundColors[key] === '') &&
        key in defaultGeneralSettings.backgroundColors
      ) {
        newGeneralSettings.backgroundColors[key] = defaultGeneralSettings.backgroundColors[key]
      }
    })
  }

  return newGeneralSettings
}
